import React, { useState, useEffect } from 'react';
import { Send, Loader2 } from 'lucide-react';
import CountryCodeSelect, { countryCodes } from './CountryCodeSelect';
import { useGeo } from '../context/GeoContext';
import { submitLead } from '../services/api';

const ContactForm = ({ title = "Get Started Today", buttonText = "Submit", packageTitle = '', packageAmount = 0 }) => {
  const geoData = useGeo();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (geoData?.data?.countryCode) {
      const match = countryCodes.find(c => c.label === geoData.data.countryCode);
      if (match) {
        setFormData(prev => ({ ...prev, countryCode: match.code }));
      }
    }
  }, [geoData]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.email)) newErrors.email = 'Please enter a valid email address (e.g., user@example.com)';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (formData.phone.replace(/[^0-9]/g, '').length !== 10) {
      newErrors.phone = 'Please enter exactly 10 digits after the country code';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const digitsOnly = value.replace(/[^0-9]/g, '');
      if (digitsOnly.length <= 10) {
        setFormData(prev => ({ ...prev, phone: digitsOnly }));
      }
      if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (apiError) setApiError('');
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'email' && value.trim()) {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(value)) setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' }));
    }
    if (name === 'phone' && value.trim()) {
      if (value.replace(/[^0-9]/g, '').length !== 10) setErrors(prev => ({ ...prev, phone: 'Please enter exactly 10 digits after the country code' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setIsSubmitting(true);
    setApiError('');

    try {
      const selected = countryCodes.find(c => c.code === formData.countryCode) || countryCodes[0];
      const result = await submitLead({
        name: formData.fullName,
        email: formData.email,
        countryDialCode: formData.countryCode.replace('+', ''),
        country: selected.label,
        phoneNumber: formData.phone,
        description: formData.message,
        packageTitle,
        packageAmount,
        geoData,
      });

      if (result.success) {
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        setApiError(result.error);
      }
    } catch (err) {
      setApiError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-xl p-6 md:p-8">
      <h3 className="text-2xl font-bold text-slate-900 mb-6">{title}</h3>
      {apiError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md" data-testid="api-error">
          <p className="text-sm text-red-700">{apiError}</p>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
          <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-black bg-white ${errors.fullName ? 'border-red-500' : 'border-slate-300'}`}
            placeholder="John Doe" data-testid="input-fullname" />
          {errors.fullName && <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1">Email Address *</label>
          <input type="text" id="email" name="email" value={formData.email} onChange={handleChange} onBlur={handleBlur}
            className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-black bg-white ${errors.email ? 'border-red-500' : 'border-slate-300'}`}
            placeholder="john@example.com" data-testid="input-email" />
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </div>

        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">Phone Number *</label>
          <div className="flex gap-2">
            <CountryCodeSelect value={formData.countryCode} onChange={(code) => handleChange({ target: { name: 'countryCode', value: code } })} className="w-32" />
            <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} onBlur={handleBlur} maxLength={10}
              className={`flex-1 px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-black bg-white ${errors.phone ? 'border-red-500' : 'border-slate-300'}`}
              placeholder="1234567890" data-testid="input-phone" />
          </div>
          {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1">Additional Information</label>
          <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows="4"
            className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-black bg-white"
            placeholder="Tell us more about your needs..." data-testid="input-message" />
        </div>

        <button type="submit" disabled={isSubmitting} data-testid="form-submit-btn"
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-md transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed">
          {isSubmitting ? <><Loader2 className="h-5 w-5 animate-spin" /><span>Submitting...</span></> : <><span>{buttonText}</span><Send className="h-5 w-5" /></>}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
